import * as React from "react";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { UitkPrimaryFloatingButton, UitkTertiaryButton, UitkButtonSize } from "uitk-react-button";
import { liveAnnounce, Level } from "uitk-react-utils";
import { UitkRadio, UitkRadioGroup } from "uitk-react-switch";
import { UitkLink, UitkLinkAlign, UitkLinkTypeSize } from "uitk-react-link";
import { UitkIcon } from "uitk-react-icons";
import { Room } from "./Room";
import { locKeys } from "../l10n";
import { TravelersPickerProps, AgeOption } from "../typings";
import { InfantSeating } from "../InfantSeating";
import { travelersMetadata, travelersSummary } from "../utils";
import { useLocalization } from "@shared-ui/localization-context";
import { RouteStore } from "bernie-core";
import { MobXProviderContext, inject } from "mobx-react";
import { getTracker } from "utils/TrackingUtils";

export const TravelersPicker = inject("analytics")((props: TravelersPickerProps) => {
  const childAgeOptions: AgeOption[] = [];
  const infantAgeOptions: AgeOption[] = [];
  const { formatText } = useLocalization();
  const routeStore: RouteStore = React.useContext(MobXProviderContext).routeStore;

  const populateAgeOptions = (
    minAge: number,
    maxAge: number,
    youngestAgeTextToken: string,
    ageOptions: AgeOption[]
  ) => {
    for (let i = minAge; i <= maxAge; i++) {
      const name: string = i === minAge && !!youngestAgeTextToken ? formatText(youngestAgeTextToken) : i.toString();
      ageOptions.push({
        value: i.toString(),
        name,
      });
    }
  };

  populateAgeOptions(
    props.config.minChildAge,
    props.config.maxChildAge,
    props.config.youngestChildAgeTextToken,
    childAgeOptions
  );
  populateAgeOptions(
    props.config.minInfantAge,
    props.config.maxInfantAge,
    props.config.youngestInfantAgeTextToken,
    infantAgeOptions
  );

  const onAddRoom = () => {
    props.setTravelers({
      rooms: props.travelers.rooms.concat({ adults: 1, children: [], infants: [] }),
      infantSeating: props.travelers.infantSeating,
    });
    liveAnnounce(formatText(locKeys.roomAdded, props.travelers.rooms.length + 1), Level.POLITE);
    props.setTotalRoomRefs(props.totalRoomRefs + 1);

    tracker.trackEvent(`${_guests}add-room`);
  };

  const onInfantSeatingChange = (event: React.ChangeEvent<HTMLFormElement>) => {
    props.setTravelers({
      rooms: props.travelers.rooms,
      infantSeating: event.currentTarget.value,
    });
  };

  const validatePickerWithFlight = () =>
    props.lobState?.validateLessThanNTravelers!() &&
    props.lobState?.validateUnattendedInfantInLap!() &&
    props.lobState?.validateInfantsPerTraveler!();

  const validatePickerWithoutFlight = () =>
    props.lobState?.validateLessThanNTravelers && props.lobState?.validateLessThanNTravelers();

  const onSave = () => {
    props.onSave(props.travelers);
    const dialogHasNoErrors = props.config.withFlight ? validatePickerWithFlight() : validatePickerWithoutFlight();
    if (dialogHasNoErrors) {
      props.setFormError("");
      routeStore.goBack();
    } else {
      const hotelTravelersInvalidKey = props.lobState?.hotelTravelersInvalidKey
        ? props.lobState.hotelTravelersInvalidKey
        : "";

      const nonHotelTravelersInvalidKey = props.lobState?.nonHotelTravelersInvalidKey
        ? props.lobState.nonHotelTravelersInvalidKey
        : "";
      props.setFormError(props.config.withRooms ? hotelTravelersInvalidKey : nonHotelTravelersInvalidKey);
    }
  };

  const tracker = getTracker(props.lobState.trackingEnabled, props.analytics, props.lobState.moduleName);
  const _guests = "guests.";

  const { numOfAdults, numOfChildren, numOfInfants } = travelersMetadata(props.travelers);
  const numOfTravelers = numOfAdults + numOfChildren + (props.config.withInfants ? numOfInfants : 0);
  const showAddRoomButton: boolean = props.config.withRooms && props.travelers.rooms.length < props.config.maxRooms;
  const showInfantSeatingOptions: boolean = props.config.withInfants && props.config.withFlight && !!numOfInfants;
  const showAirlineAgeRules: boolean =
    props.config.withFlight && (!!numOfChildren || (props.config.withInfants && !!numOfInfants));

  return (
    <>
      <section className="uitk-cell all-x-padding-four all-t-padding-half all-b-padding-twelve uitk-scroll-vertical">
        {props.travelers.rooms.map((room, index) => (
          <Room
            setTravelers={props.setTravelers}
            travelers={props.travelers}
            key={index}
            index={index}
            room={room}
            config={props.config}
            roomRefs={props.roomRefs}
            totalRoomRefs={props.totalRoomRefs}
            setTotalRoomRefs={props.setTotalRoomRefs}
            childAgeOptions={childAgeOptions}
            infantAgeOptions={infantAgeOptions}
            lobState={props.lobState}
          />
        ))}
        {showAddRoomButton && (
          <div className="addRoomButton uitk-type-right all-t-padding-four">
            <UitkTertiaryButton onClick={onAddRoom} text={formatText(locKeys.addRoom)} />
          </div>
        )}
        {showInfantSeatingOptions && (
          <div className="airlineChildSeating uitk-cell all-cell-1-1 all-y-gutter-one">
            <fieldset>
              <legend className="uitk-type-300 uitk-type-bold">{formatText(locKeys.airlineChildSeatingMessage)}</legend>
              <UitkRadioGroup
                group="ChildSeating"
                onChange={onInfantSeatingChange}
                selected={props.travelers.infantSeating}
              >
                <UitkRadio
                  id="ChildOnLap"
                  labelClassName="all-r-padding-six"
                  label={formatText(locKeys.airlineSeatingOnLap)}
                  value={InfantSeating.ON_LAP}
                />
                <UitkRadio
                  id="ChildInSeat"
                  labelClassName="all-r-padding-six"
                  label={formatText(locKeys.airlineSeatingInSeat)}
                  value={InfantSeating.IN_SEAT}
                />
              </UitkRadioGroup>
            </fieldset>
          </div>
        )}
        {showAirlineAgeRules && (
          <div className="uitk-cell all-cell-1-1">
            <UitkLink iconPos={UitkLinkAlign.RIGHT} type={UitkLinkTypeSize.SMALL} className="airlineAgeRules">
              <a
                href="/p/info-main/h_fw_default?rhtx=HTX_FLTSRCH_RT#travs"
                id="airline-age-rules"
                target="_blank"
                rel="noopener"
              >
                {formatText(locKeys.airlineAgeRules)}
                <UitkIcon id="airline-age-rules-opens-in-new" name="open_in_new" title="Opens in a new window" />
              </a>
            </UitkLink>
          </div>
        )}
        <div className="all-t-padding-two all-b-padding-six" />
      </section>
      <UitkScrim className="guestsDoneBtn" type={UitkScrimType.BUTTON}>
        <UitkPrimaryFloatingButton
          onClick={onSave}
          text={formatText(locKeys.doneLabel)}
          subText={travelersSummary(props.travelers.rooms, props.config.withRooms, numOfTravelers)}
          size={UitkButtonSize.LARGE}
          isFullWidth={true}
        />
      </UitkScrim>
    </>
  );
});
